import { ITEMS_PER_PAGE } from "#constants";
import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import {
  useDeleteFlow,
  useExportFlows,
  useListFlows,
} from "#hooks/adapters/useFlows";
import { useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import EditFlowDialog from "#src/batteries-included-components/Dialogs/EditFlowDialog";
import { FlowsTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  ItemAction,
  StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  FlowAdapter,
  FlowAdapterQueryKeys,
  FlowType,
  FlowV2Type,
  ResourceDefinitions,
  SortDirection,
  type FlowGetListRequestType,
  type GroupedByEntryType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  toFlattenedObject,
} from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

export const FlowsTablePanel = ({
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  groupByTablesStateStorageKey,
  isWithCustomAttributes,
  isWithTableActions,
  pageSize = ITEMS_PER_PAGE,
}: {
  isWithCustomAttributes?: boolean;
  isWithTableActions?: boolean;
  pageSize?: number;
} & StorageKeys) => {
  const { localize } = useLocalization();
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingCompanyConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);

  const [viewFilters] = useStickyState<{
    period?: {
      from?: string | Date;
      to?: string | Date;
    };
  }>({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState<FlowGetListRequestType["filters"]>(
    {},
    filterConfigStorageKey
  );
  const rawFilters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const { period: periodFilter, ...otherFilters } = rawFilters;

  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [flowToDelete, setFlowToDelete] = useState<FlowType>();
  const { customAttributeHeaders, isLoading: isCustomAttrsLoading } =
    useCustomAttributeFilters({
      assetType: AssetType.FLOW,
      enabled: isWithCustomAttributes,
      groupable: true,
    });

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
      pageSize,
    },
    otherFilters,
    tableStateStorageKey
  );

  const flowParams: Parameters<typeof FlowAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: toFlattenedObject(otherFilters),
    meta: {
      period: getYearMonthFromDateRange(
        periodFilter as {
          from?: string | Date;
          to?: string | Date;
        }
      ),
    },
  };

  const { data, isLoading, isFetching } = useListFlows(flowParams);
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...flowParams,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? flowParams)
  );

  const flowGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: FlowAdapterQueryKeys.getList(d.payload),
      queryFn: () => FlowAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? flowGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const { isLoading: isLoadingFlowHeaders, headers: flowHeaders } =
    useFlowHeaders({
      groupable: true,
    });
  const exportFlows = useExportFlows(flowParams);
  const { mutate: deleteFlow } = useDeleteFlow();

  const deleteFlowOnClick = () => {
    if (flowToDelete) {
      deleteFlow({ id: flowToDelete.id });
    }
    setFlowToDelete(undefined);
  };

  const rowActionItems = ({ item }: { item: FlowV2Type }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setAssetIdToEdit(item.id),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => setFlowToDelete(item as FlowType),
        icon: "trash",
      },
    },
  ];

  const headers = useMemo(
    () => [
      ...flowHeaders,
      ...(isWithCustomAttributes ? customAttributeHeaders : []),
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [flowHeaders, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const actions = [
    <PermissionAwareExportButton
      key="export-devices"
      onClick={() => {
        exportFlows.mutate();
      }}
      isExporting={exportFlows.isLoading}
    />,
    isDataIngestionEnabled ? (
      <ImportDataAction
        key="import-flow"
        resource={{
          ...ResourceDefinitions.flow,
          label: {
            singular: localize(ResourceDefinitions.flow.label.singular),
            plural: localize("flow_plural"),
          },
        }}
      />
    ) : null,
  ];

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: flowGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
    getItemActions: rowActionItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Flows list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingCompanyConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          actionRow: isWithTableActions ? actions : undefined,
          isFluidY: false,
        }}
        filterComponent={
          <FlowsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            hasCustomAttributeFilters={isWithCustomAttributes}
            hasSubAssetFilters
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          intent: "simplicity-first",
          headers: headers as Array<HeaderType<FlowV2Type>>,
          items,
          getItemId: (item) => item.id,
          isLoading: isLoading || isCustomAttrsLoading || isLoadingFlowHeaders,
          isBusy: isFetching,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries ?? 0,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions: rowActionItems,
        }}
        {...groupByDataTableProps}
      />
      <Dialog
        isOpen={!!flowToDelete}
        onClose={() => setFlowToDelete(undefined)}
        title={`Delete "${flowToDelete?.name}"`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={deleteFlowOnClick}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete this flow? This action cannot be
          undone.
        </div>
      </Dialog>
      <EditFlowDialog
        defaultPeriod={
          getYearMonthFromDateRange(
            periodFilter as {
              from?: string | Date;
              to?: string | Date;
            }
          ) ?? ""
        }
        onClose={() => setAssetIdToEdit(undefined)}
        flowId={assetIdToEdit}
      />
    </>
  );
};
